import { useEffect, useState } from "react";
import { fieldMe, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WiseAceLogo } from "@/components/WiseAceLogo";
import { StockOrders } from "@/components/StockOrders";
import { Jobs } from "@/components/Jobs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Package, LogOut, HardHat, ChevronLeft, Wrench } from "lucide-react";

export default function FieldDashboard() {
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [view, setView] = useState("home");

  useEffect(() => { fieldMe().then(setMe).catch((e) => toast.error(apiError(e))); }, []);
  const doLogout = async () => { await logout(); window.location.href = "/login"; };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="field-dashboard">
      <header className="text-white px-5 py-5" style={{ background: "linear-gradient(155deg,#0B2F2A 0%,#0B4A40 100%)" }}>
        <div className="flex items-center justify-between">
          {view === "home" ? <WiseAceLogo light size={30} /> : (
            <button onClick={() => setView("home")} className="text-slate-200 flex items-center gap-1 text-sm" data-testid="field-back"><ChevronLeft className="w-5 h-5" /> Back</button>
          )}
          <button onClick={doLogout} className="text-slate-200 flex items-center gap-1.5 text-sm" data-testid="field-logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-[#C9A227]"><HardHat className="w-6 h-6" /></div>
          <div>
            <div className="text-lg font-bold leading-tight" data-testid="field-name">{me?.name || "…"}</div>
            <div className="text-sm text-slate-300">{me?.job_title}{me?.team?.name ? ` · ${me.team.name}` : ""}</div>
            {me?.team?.team_leader_name && <div className="text-xs text-slate-400">Team Leader: {me.team.team_leader_name}</div>}
          </div>
        </div>
        <div className="mt-3 text-xs uppercase tracking-widest text-[#C9A227] font-semibold">Plumbing Operations</div>
      </header>

      <main className="p-4 max-w-md mx-auto">
        {view === "home" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setView("jobs")} className="bg-white rounded-2xl border border-slate-200 p-5 text-left active:scale-95 transition-transform" data-testid="field-card-my-jobs">
                <div className="w-11 h-11 rounded-xl bg-[#0B4A40]/10 flex items-center justify-center text-[#0B4A40]"><Wrench className="w-6 h-6" /></div>
                <div className="mt-3 font-bold text-[#0B132B]">My Jobs</div>
                <div className="text-xs text-slate-500">Assigned work</div>
              </button>
              <button onClick={() => setView("stock")} className="bg-white rounded-2xl border border-slate-200 p-5 text-left active:scale-95 transition-transform" data-testid="field-card-my-stock">
                <div className="w-11 h-11 rounded-xl bg-[#0B4A40]/10 flex items-center justify-center text-[#0B4A40]"><Package className="w-6 h-6" /></div>
                <div className="mt-3 font-bold text-[#0B132B]">Stock Orders</div>
                <div className="text-xs text-slate-500">Request & track materials</div>
              </button>
            </div>
            <Button onClick={() => setView("stock")} className="w-full mt-4 h-14 text-base bg-[#C9A227] hover:bg-[#b08f1f] text-[#0B2F2A] font-bold" data-testid="field-request-stock">
              <Package className="w-5 h-5 mr-2" /> Request Stock
            </Button>
          </>
        )}
        {view === "jobs" && (
          <div data-testid="field-jobs-view">
            <h2 className="font-bold text-lg text-[#0B132B] mb-3">My Jobs</h2>
            <Jobs manage={false} accentClass="bg-[#0B4A40] hover:bg-[#0B2F2A]" />
          </div>
        )}
        {view === "stock" && (
          <div data-testid="field-stock-view">
            <h2 className="font-bold text-lg text-[#0B132B] mb-3">Stock Orders</h2>
            <StockOrders manage={false} accentClass="bg-[#0B4A40] hover:bg-[#0B2F2A]" />
          </div>
        )}
      </main>
    </div>
  );
}
